import { type FC, type FormEvent, useState } from 'react';

import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@/ui';

import {
  type SchedulingAssistant,
  schedulingAssistantErrorCode,
  type SchedulingAssistantErrorCode,
  type SchedulingAssistantOwner,
  useAddSchedulingAssistantMutation,
  useGetSchedulingAssistantsQuery,
  useRemoveSchedulingAssistantMutation,
  useResendSchedulingAssistantConfirmationMutation,
} from '@/store/api/schedulingAssistantApi';

interface SchedulingAssistantsProps {
  // First name of the member whose assistants Ops are managing.
  memberFirstName?: string;
  owner: SchedulingAssistantOwner;
}

// Copy reads "you" on the member's own profile and names the member in admin.
interface OwnerCopy {
  has: string;
  possessive: string;
  sessionsOwner: string;
  subject: string;
}

const ownerCopy = (
  owner: SchedulingAssistantOwner,
  memberFirstName = '',
): OwnerCopy => {
  if (owner.kind === 'self') {
    return {
      has: 'You have',
      possessive: 'your',
      sessionsOwner: 'your',
      subject: 'You',
    };
  }
  const name = memberFirstName || 'This member';
  return {
    has: `${name} has`,
    possessive: `${name}’s`,
    sessionsOwner: 'their',
    subject: name,
  };
};

type RowNotice = { severity: 'success' | 'error'; text: string };

// Moments isn't available for this viewer: nothing to manage, so no card.
const UNAVAILABLE_STATUSES: readonly (number | string)[] = [
  'MOMENTS_NOT_READY',
  'MOMENTS_BASE_URL_MISSING',
  'MOMENTS_TOKEN_MISSING',
  'MOMENTS_PRIVATE_BOUNDARY',
  'CANCELLED',
  428,
];

const GENERIC_ERROR = 'Something went wrong. Please try again.';

const addErrorMessage = (
  code: SchedulingAssistantErrorCode | null,
  limit: number,
  copy: OwnerCopy,
): string => {
  switch (code) {
    case 'invalid_email':
      return 'Enter a single email address, like sam@example.com.';
    case 'invalid_display_name':
      return 'Enter their name (up to 100 characters, no email address).';
    case 'assistant_exists':
      return 'This email is already a scheduling assistant.';
    case 'assistant_limit':
      return `${copy.has} the maximum of ${limit} scheduling assistants. Remove one to add another.`;
    case 'member_not_found':
      return `${copy.subject} isn’t set up for scheduling yet.`;
    default:
      return GENERIC_ERROR;
  }
};

const resendErrorMessage = (
  code: SchedulingAssistantErrorCode | null,
  assistant: SchedulingAssistant,
): string => {
  switch (code) {
    case 'resend_limit':
      return `We’ve sent ${assistant.displayName} the most confirmation emails allowed in a day. Try again tomorrow.`;
    case 'not_pending':
      return `${assistant.displayName} has already confirmed.`;
    case 'not_found':
      return `${assistant.displayName} is no longer an assistant.`;
    default:
      return GENERIC_ERROR;
  }
};

const SchedulingAssistants: FC<SchedulingAssistantsProps> = ({
  memberFirstName,
  owner,
}) => {
  const { data, error, isError, isLoading, refetch } =
    useGetSchedulingAssistantsQuery(owner);
  const [addAssistant, { isLoading: isAdding }] =
    useAddSchedulingAssistantMutation();
  const [removeAssistant] = useRemoveSchedulingAssistantMutation();
  const [resendConfirmation] =
    useResendSchedulingAssistantConfirmationMutation();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [addNotice, setAddNotice] = useState<RowNotice | null>(null);
  const [rowNotices, setRowNotices] = useState<Record<string, RowNotice>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmingRemoveId, setConfirmingRemoveId] = useState<string | null>(
    null,
  );

  const listErrorStatus = (error as { status?: number | string } | undefined)
    ?.status;
  if (
    isError &&
    listErrorStatus !== undefined &&
    UNAVAILABLE_STATUSES.includes(listErrorStatus)
  ) {
    return null;
  }

  const copy = ownerCopy(owner, memberFirstName);
  const setRowNotice = (id: string, notice: RowNotice | null) =>
    setRowNotices((current) => {
      const next = { ...current };
      if (notice) next[id] = notice;
      else delete next[id];
      return next;
    });

  const onAdd = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAddNotice(null);
    try {
      const added = await addAssistant({
        displayName: displayName.trim(),
        email: email.trim(),
        owner,
      }).unwrap();
      setDisplayName('');
      setEmail('');
      setAddNotice({
        severity: 'success',
        text: `We emailed ${added.displayName} a link to confirm.`,
      });
    } catch (addError) {
      setAddNotice({
        severity: 'error',
        text: addErrorMessage(
          schedulingAssistantErrorCode(addError),
          data?.limit ?? 0,
          copy,
        ),
      });
    }
  };

  const onResend = async (assistant: SchedulingAssistant) => {
    setBusyId(assistant.id);
    setRowNotice(assistant.id, null);
    try {
      await resendConfirmation({ id: assistant.id, owner }).unwrap();
      setRowNotice(assistant.id, {
        severity: 'success',
        text: `We sent ${assistant.displayName} a new confirmation link.`,
      });
    } catch (resendError) {
      setRowNotice(assistant.id, {
        severity: 'error',
        text: resendErrorMessage(
          schedulingAssistantErrorCode(resendError),
          assistant,
        ),
      });
    } finally {
      setBusyId(null);
    }
  };

  const onRemove = async (assistant: SchedulingAssistant) => {
    setBusyId(assistant.id);
    setRowNotice(assistant.id, null);
    try {
      await removeAssistant({ id: assistant.id, owner }).unwrap();
      setConfirmingRemoveId(null);
    } catch (removeError) {
      // Already removed elsewhere: the refetched list drops the row.
      if (schedulingAssistantErrorCode(removeError) !== 'not_found') {
        setRowNotice(assistant.id, { severity: 'error', text: GENERIC_ERROR });
      }
    } finally {
      setBusyId(null);
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-4">
          <CircularProgress aria-label="Loading scheduling assistants" />
        </div>
      );
    }
    if (isError || !data) {
      const code = schedulingAssistantErrorCode(error);
      return (
        <Alert
          action={
            code === 'member_not_found' ? undefined : (
              <Button color="error" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            )
          }
          severity="error"
        >
          {code === 'member_not_found'
            ? addErrorMessage(code, 0, copy)
            : 'We couldn’t load scheduling assistants.'}
        </Alert>
      );
    }

    const atLimit = data.assistants.length >= data.limit;
    return (
      <>
        {data.assistants.length > 0 && (
          <ul className="mb-4 divide-y divide-cement-200">
            {data.assistants.map((assistant) => {
              const notice = rowNotices[assistant.id];
              const isBusy = busyId === assistant.id;
              const isPending = assistant.status === 'pending';
              return (
                <li key={assistant.id} className="py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Typography variant="body1">
                        {assistant.displayName}
                      </Typography>
                      <Typography
                        className="break-all text-cement-400"
                        variant="body2"
                      >
                        {assistant.email}
                      </Typography>
                    </div>
                    <Chip
                      color={isPending ? 'warning' : 'success'}
                      label={isPending ? 'Pending' : 'Active'}
                      size="small"
                    />
                  </div>
                  {isPending && (
                    <Typography
                      className="mt-1 text-cement-400"
                      variant="body2"
                    >
                      Waiting for them to confirm from the email we sent.
                    </Typography>
                  )}
                  {confirmingRemoveId === assistant.id ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Typography variant="body2">
                        Remove {assistant.displayName}? They’ll lose access
                        right away.
                      </Typography>
                      <Button
                        color="error"
                        disabled={isBusy}
                        size="small"
                        variant="contained"
                        onClick={() => onRemove(assistant)}
                      >
                        Remove
                      </Button>
                      <Button
                        disabled={isBusy}
                        size="small"
                        onClick={() => setConfirmingRemoveId(null)}
                      >
                        Keep
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-2 flex gap-2">
                      {isPending && (
                        <Button
                          disabled={isBusy}
                          size="small"
                          variant="outlined"
                          onClick={() => onResend(assistant)}
                        >
                          Resend email
                        </Button>
                      )}
                      <Button
                        aria-label={`Remove ${assistant.displayName}`}
                        disabled={isBusy}
                        size="small"
                        onClick={() => setConfirmingRemoveId(assistant.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                  {notice && (
                    <Alert className="mt-2" severity={notice.severity}>
                      {notice.text}
                    </Alert>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {atLimit ? (
          <Typography className="text-cement-400" variant="body2">
            {copy.has} the maximum of {data.limit} scheduling assistants. Remove
            one to add another.
          </Typography>
        ) : (
          <form className="space-y-3" noValidate onSubmit={onAdd}>
            <TextField
              label="Assistant’s name"
              size="small"
              value={displayName}
              fullWidth
              onChange={(event) => setDisplayName(event.target.value)}
            />
            <TextField
              label="Assistant’s email"
              size="small"
              type="email"
              value={email}
              fullWidth
              onChange={(event) => setEmail(event.target.value)}
            />
            <Button
              disabled={isAdding || !displayName.trim() || !email.trim()}
              type="submit"
              variant="contained"
            >
              Add assistant
            </Button>
          </form>
        )}
        {addNotice && (
          <Alert className="mt-3" severity={addNotice.severity}>
            {addNotice.text}
          </Alert>
        )}
      </>
    );
  };

  return (
    <Card>
      <CardContent>
        <Typography className="mb-2" variant="subtitle1">
          Scheduling assistant
        </Typography>
        <Typography className="mb-4 text-cement-400" variant="body2">
          Someone who manages {copy.possessive} calendar can reschedule or
          cancel {copy.sessionsOwner} coaching sessions from links we email
          them. They don’t need a Mento account, and they confirm their email
          before they can make changes.
        </Typography>
        {renderBody()}
      </CardContent>
    </Card>
  );
};

export default SchedulingAssistants;
